/**
 * Reads the device safe-area insets exposed by CSS `env(safe-area-inset-*)`
 * (set up in index.html with `viewport-fit=cover`). On a desktop browser these
 * are all 0; on a notched iPhone they keep the UI clear of the notch / home bar.
 *
 * DOM is touched only behind feature guards so this module is import-safe in the
 * node test environment (it just returns zero insets there).
 */
import { NO_INSETS, type SafeAreaInsets } from './geometry.js';

/**
 * A hidden probe element whose padding is driven by `env(safe-area-inset-*)`.
 * Reading its computed padding is the portable way to get the live inset px.
 */
let probe: HTMLDivElement | null = null;

function ensureProbe(): HTMLDivElement | null {
  if (typeof document === 'undefined') return null;
  if (probe && probe.isConnected) return probe;
  const el = document.createElement('div');
  el.setAttribute('aria-hidden', 'true');
  el.style.position = 'fixed';
  el.style.left = '0';
  el.style.top = '0';
  el.style.width = '0';
  el.style.height = '0';
  el.style.visibility = 'hidden';
  el.style.pointerEvents = 'none';
  el.style.paddingTop = 'env(safe-area-inset-top, 0px)';
  el.style.paddingRight = 'env(safe-area-inset-right, 0px)';
  el.style.paddingBottom = 'env(safe-area-inset-bottom, 0px)';
  el.style.paddingLeft = 'env(safe-area-inset-left, 0px)';
  document.body.appendChild(el);
  probe = el;
  return el;
}

function px(value: string): number {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : 0;
}

/** Read the current safe-area insets (zero when no DOM / unsupported). */
export function readSafeAreaInsets(): SafeAreaInsets {
  const el = ensureProbe();
  if (!el || typeof getComputedStyle !== 'function') return NO_INSETS;
  const cs = getComputedStyle(el);
  return {
    top: px(cs.paddingTop),
    right: px(cs.paddingRight),
    bottom: px(cs.paddingBottom),
    left: px(cs.paddingLeft),
  };
}
